import { useQuery } from '@tanstack/react-query'
import { fetchCustomEndpointOnce } from './customEndpointFetch'

type LastReadingRow = { deviceId: number; lastReadingAt: number }

/**
 * Per-device time of the last real (non-sentinel) reading, independent of any
 * display window.
 *
 * WHY THIS EXISTS (field report 2026-08-19, Faithfull Friends via Dustin): the
 * Clinical Dashboard derived "last reading" from its own clinic-wide metrics
 * fetch, capped at `perPage: 10000`. For a vet with a handful of patients that
 * window covers everything — but fleet-wide those 10,000 rows span about FIVE
 * HOURS. On an admin-scoped dashboard any pet silent longer than that dropped
 * out of the fetch entirely and arrived with `lastUpdatedAt: undefined`, which
 * `deriveCgmStatus` deliberately treats as "not evidence of a stopped sensor"
 * and reports Active. Pet Reuben (device A0009XR) therefore read Active with a
 * 31-day-old last reading. Measured on prod at the time: 778 of 882 pets with
 * an activated device were more than 48h silent yet displayed Active.
 *
 * `null` means UNKNOWN (still loading, or the request failed) — never "no
 * device has ever reported". Callers must keep their existing behavior while
 * this is null rather than concluding silence, otherwise a transient failure
 * would mark the whole fleet Inactive, which is the same bug with the sign
 * flipped.
 */
export const useDeviceLastReadings = (): {
  lastReadings: Map<number, Date> | null
  isLoading: boolean
} => {
  const { data, isPending } = useQuery<Map<number, Date>>({
    queryKey: ['devices-last-reading'],
    queryFn: async ({ signal }) => {
      const res = await fetchCustomEndpointOnce('/devices-last-reading', signal)
      if (!res || !res.ok) {
        // Throw rather than resolving empty, so react-query does not CACHE a
        // degraded answer as success — an empty map is indistinguishable from
        // "nothing ever reported" at the call site.
        throw new Error('devices-last-reading unavailable')
      }
      const body = await res.json()
      if (!Array.isArray(body?.lastReadings)) {
        throw new Error('devices-last-reading malformed response')
      }
      const map = new Map<number, Date>()
      for (const row of body.lastReadings as LastReadingRow[]) {
        // lastReadingAt is unix SECONDS (metrics.timestamp), matching
        // getMetricDate's own conversion. A row that fails this guard is
        // skipped rather than stored as an Invalid Date / 1970.
        if (typeof row?.deviceId !== 'number' || !Number.isFinite(row?.lastReadingAt)) continue
        map.set(row.deviceId, new Date(row.lastReadingAt * 1000))
      }
      return map
    },
    // The fetcher already does the quiet 401-retry-once; don't stack
    // react-query retries (would multiply calls on genuine errors).
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

  return { lastReadings: data ?? null, isLoading: isPending }
}
